import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, clearAuthAndRedirect } from '@aimsos/auth';
import { useTheme } from '@aimsos/theme';
import { useTranslation } from '@aimsos/i18n';
import {
  AimsLogo,
  Topbar,
  TopbarGlobalSearchModal,
  TopbarSettingsMenu,
  filterSettingsSections,
  topbarShellSlotClass,
  applyCategoryToFilterIds,
  buildActiveFiltersFromIds,
  buildGlobalSearchCategories,
  buildGlobalSearchTypeEntities,
  getCategoryIdFromTypeFilter,
  globalSearchLabelsFixture,
  globalSearchModalPlaceholderFixture,
  globalSearchRecentItemsFixture,
  globalSearchSuggestedActionsFixture,
  useToast,
} from '@aimsos/ui';
import { useSettingsActor } from '../hooks/useSettingsActor.js';
import { useWorkspaces } from '../hooks/useWorkspaces.js';
import { findActiveSection, NAV_CONFIG } from './navigationConfig.js';
import { applySettingsPermissions } from './settingsMenuPermissions.js';
import { getSettingsRoute } from './settingsRoutes.js';
import { Bell, CircleHelp, Languages, Settings, Sparkles, UserRound } from 'lucide-react';

/**
 * Core Shell top navigation.
 * Owns the dark mode toggle and user session actions.
 */
const ShellTopNav = ({ onMenuClick, onOpenAssistant }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  // useAuth() (not useAuth0() directly) so name/email display correctly on
  // a {slug}.{rootDomain} landed on via a cross-subdomain handoff, where
  // Auth0 SDK itself has no session (see profileCache.js).
  const { user } = useAuth();
  const { t, i18n } = useTranslation('core-shell');
  const toast = useToast();
  // Memoized so the object reference is stable — passing a new literal on every
  // render would invalidate the useMemo in useWorkspaces on every TopNav render,
  // cascading through accountMenu and TopbarRightMenu.
  const workspaceLabels = useMemo(
    () => ({ current: t('workspaces.current'), switch: t('workspaces.switch') }),
    [t],
  );
  const workspaces = useWorkspaces({
    fallbackName: t('nav.workspaces'),
    labels: workspaceLabels,
  });
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilterIds, setSelectedFilterIds] = useState([]);

  // Who sees which Settings entry is decided by permissions (CT-5569), not by role: `isAdmin`
  // only keeps the role-based stripping of `filterSettingsSections` out of the way, and
  // `applySettingsPermissions` removes what the viewer may not open. The actor comes from a
  // mock until the permissions contract exists (ARP/backend).
  const handleSettingsActorError = useCallback(
    () => toast.error({ title: t('topNav.settingsMenu.permissionsError'), canClose: true }),
    [toast, t],
  );
  const settingsActor = useSettingsActor({ onError: handleSettingsActorError });
  const settingsSections = useMemo(
    () =>
      applySettingsPermissions(
        filterSettingsSections({ isAdmin: true, hasWorkspaceAccess: true, t }),
        settingsActor,
      ),
    [t, settingsActor],
  );

  const settingsMenuLabels = useMemo(
    () => ({
      title: t('topNav.settingsMenu.title'),
      searchPlaceholder: t('topNav.settingsMenu.searchPlaceholder'),
      noResults: t('topNav.settingsMenu.noResults'),
    }),
    [t],
  );

  // clearAuthAndRedirect (not useAuth0().logout directly) so the
  // cross-subdomain "logout everywhere" marker gets written and our own
  // api_access_token/user_uuid/workspace_uuid are cleared — see
  // globalLogout.js. Calling Auth0's logout() alone only clears Auth0's own
  // per-origin session cache, leaving every other {slug}.{rootDomain}
  // subdomain (and the root domain) still "logged in" from this app's POV.
  const handleLogout = useCallback(() => { clearAuthAndRedirect(); }, []);

  const applications = useMemo(
    () => NAV_CONFIG.map((section) => ({
      id: section.id,
      // nav.* keys are camelCase; section ids are kebab-case (agentic-studio,
      // admin-studio), so convert before resolving the label.
      title: t(`nav.${section.id.replace(/-([a-z])/g, (_, c) => c.toUpperCase())}`),
      description: section.description,
      icon: section.icon,
      path: section.path,
    })),
    [t],
  );

  const selectedApplicationId = useMemo(() => {
    const pathname = location.pathname || '';
    return findActiveSection(pathname)?.id ?? NAV_CONFIG[0].id;
  }, [location.pathname]);

  const handleSelectApplication = useCallback((application) => {
    if (application?.path) navigate(application.path);
  }, [navigate]);

  // Tenants come from the workspaces hook (always loaded from the back;
  // falls back to a single-tenant placeholder if the API call fails or
  // returns an empty list so the rest of the shell keeps rendering).
  const {
    tenants,
    currentTenantId: activeTenantId,
    switchTo,
    isLoading: workspacesLoading,
    isRefetching: workspacesRefetching,
    searchKeyword,
    onSearchChange,
    hasMore,
    loadMore,
    isLoadingMore,
    isSwitching,
    switchError,
    clearSwitchError,
  } = workspaces;

  const handleTenantSelect = useCallback((tenant) => {
    if (!tenant?.id) return;
    switchTo(tenant.id);
  }, [switchTo]);

  // Show one error toast per failure, then immediately clear switchError.
  //
  // `toast` is intentionally excluded from the dependency array: the
  // ToastProvider rebuilds its API object on every items change, so including
  // it would cause this effect to re-run after the toast is added — while
  // switchError is still non-null — producing an infinite loop.
  // Calling clearSwitchError() right after toast.error() resets switchError
  // to null, the effect runs once more, the guard fails, and the loop stops.
  useEffect(() => {
    if (switchError !== null) {
      toast.error({ title: t('workspaces.switchError'), message: switchError || t('workspaces.switchError') });
      clearSwitchError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switchError, clearSwitchError, t]);

  const handleThemeChange = useCallback(
    (nextTheme) => {
      // The UI exposes a single "Auto" entry point. Map it to the provider's
      // `'system'` preference so we don't need a second concept.
      setTheme(nextTheme === 'auto' ? 'system' : nextTheme);
    },
    [setTheme],
  );

  const actionButtons = useMemo(
    () => [
      {
        id: 'assistant',
        icon: Sparkles,
        variant: 'assistant',
        onClick: onOpenAssistant,
        ariaLabel: t('topNav.openAssistant'),
      },
      {
        id: 'notifications',
        icon: Bell,
        onClick: () => {},
        ariaLabel: 'Notifications',
        showBadge: true,
      },
      {
        id: 'language',
        icon: Languages,
        onClick: () => { setIsLangOpen((v) => !v); setIsSearchOpen(false); setIsSettingsOpen(false); },
        ariaLabel: t('topNav.language'),
      },
      {
        id: 'settings',
        icon: Settings,
        onClick: () => { setIsSettingsOpen((v) => !v); setIsSearchOpen(false); setIsLangOpen(false); },
        ariaLabel: t('topNav.settings'),
      },
    ],
    [onOpenAssistant, t],
  );

  const accountItems = useMemo(
    () => [
      { id: 'profile',       label: t('topNav.profilePreferences'),       icon: UserRound,  onClick: () => {} },
      { id: 'notifications', label: t('topNav.notificationsPreferences'), icon: Bell,       onClick: () => {} },
      { id: 'help',          label: t('topNav.helpDocs'),                 icon: CircleHelp, onClick: () => {} },
    ],
    [t],
  );

  // Map the provider's `system` preference to the UI's "Auto" label so the
  // existing button highlight (`themeValue === 'auto'`) keeps working.
  const themeValue = theme === 'system' ? 'auto' : theme;
  const globalSearchLabels = globalSearchLabelsFixture;
  const globalSearchPlaceholder = globalSearchModalPlaceholderFixture;

  const typeEntities = useMemo(
    () => buildGlobalSearchTypeEntities(globalSearchLabels),
    [globalSearchLabels],
  );
  const categories = useMemo(
    () => buildGlobalSearchCategories(globalSearchLabels, typeEntities),
    [globalSearchLabels, typeEntities],
  );
  const activeCategory = useMemo(
    () => getCategoryIdFromTypeFilter(selectedFilterIds),
    [selectedFilterIds],
  );
  const activeFilters = useMemo(
    () => buildActiveFiltersFromIds(selectedFilterIds, globalSearchLabels, typeEntities),
    [globalSearchLabels, selectedFilterIds, typeEntities],
  );

  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
    setIsSettingsOpen(false);
    setIsLangOpen(false);
  }, []);

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    setSearchValue('');
    setSelectedFilterIds([]);
  }, []);

  // Memoize objects passed to Topbar so downstream React.memo boundaries
  // are not falsely invalidated on every render.
  const accountMenu = useMemo(
    () => ({
      user: {
        name: user?.name || user?.email || 'User',
        email: user?.email || '',
        initials:
          (user?.name || '')
            .split(' ')
            .map((part) => part[0])
            .join('')
            .slice(0, 2)
            .toUpperCase() || 'US',
      },
      tenants,
      currentTenantId: activeTenantId,
      onTenantSelect: handleTenantSelect,
      searchValue: searchKeyword,
      onSearchChange,
      hasMore,
      onLoadMore: loadMore,
      isLoadingMore,
      items: accountItems,
      isLoading: workspacesLoading || isSwitching,
      isRefetching: workspacesRefetching,
      theme: {
        value: themeValue,
        onChange: handleThemeChange,
      },
      onSignOut: handleLogout,
      initialTenantsExpanded: false,
    }),
    [
      user,
      tenants,
      activeTenantId,
      handleTenantSelect,
      onSearchChange,
      searchKeyword,
      hasMore,
      loadMore,
      isLoadingMore,
      accountItems,
      workspacesLoading,
      isSwitching,
      workspacesRefetching,
      themeValue,
      handleThemeChange,
      handleLogout,
    ],
  );

  const labels = useMemo(
    () => ({
      leftMenuTitle: t('nav.applications'),
      organization: t('nav.workspaces'),
      workspace: t('nav.workspaces'),
      theme: 'Theme',
      auto: 'Auto',
      signOut: t('topNav.signOut'),
      tenantSearchPlaceholder: 'Search',
      openMenu: 'Open menu',
    }),
    [t],
  );

  const tooltips = useMemo(
    () => ({
      actions: {
        assistant: t('topNav.openAssistant'),
        notifications: 'Notifications',
        settings: t('topNav.settings'),
      },
    }),
    [t],
  );

  return (
    <>
      {/* relative wrapper anchors TopbarSettingsMenu below the topbar */}
      <div className="relative w-full shrink-0">
        <div className={topbarShellSlotClass}>
          <Topbar
            onMenuClick={onMenuClick}
            onOpenSearch={openSearch}
            variant="default"
            brandMark={<AimsLogo size={19} />}
            studio={{ label: 'Work Surface', avatarInitials: 'WS' }}
            applications={applications}
            selectedApplicationId={selectedApplicationId}
            onSelectApplication={handleSelectApplication}
            showApplicationDescriptions={false}
            searchProps={{
              value: searchValue,
              placeholder: globalSearchPlaceholder,
              onChange: (event) => setSearchValue(event.target.value),
            }}
            actionButtons={actionButtons}
            company={{ label: t('nav.workspaces'), avatarInitials: 'WS', userBadge: 'U' }}
            accountMenu={accountMenu}
            labels={labels}
            tooltips={tooltips}
          />
        </div>

        {isLangOpen && (
          <div
            className="absolute right-[72px] top-full z-50 mt-2 w-44 overflow-hidden rounded-xl border border-[#E5E7EB] bg-white shadow-[0_8px_32px_rgba(0,0,0,0.12)] dark:border-white/10 dark:bg-[#1a2030]"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="border-b border-[#E5E7EB] px-4 py-2 dark:border-white/10">
              <p className="text-xs font-medium text-[#9CA3AF] dark:text-gray-500">{t('topNav.language')}</p>
            </div>
            {[
              { code: 'es', label: t('topNav.langEs') },
              { code: 'en', label: t('topNav.langEn') },
            ].map(({ code, label }) => (
              <button
                key={code}
                type="button"
                onClick={() => { void i18n.changeLanguage(code); setIsLangOpen(false); }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-[#111827] transition-colors hover:bg-[#F3F4F6] dark:text-gray-100 dark:hover:bg-white/[0.07]"
              >
                <Languages className="h-4 w-4 shrink-0 text-[#6B7280] dark:text-gray-400" strokeWidth={1.5} aria-hidden />
                {label}
              </button>
            ))}
          </div>
        )}

        <TopbarSettingsMenu
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          onNavigate={(id) => { navigate(getSettingsRoute(id)); setIsSettingsOpen(false); }}
          sections={settingsSections}
          labels={settingsMenuLabels}
        />
      </div>

      <TopbarGlobalSearchModal
        enableSearchShortcut
        isOpen={isSearchOpen}
        onOpen={openSearch}
        onClose={closeSearch}
        query={searchValue}
        onQueryChange={setSearchValue}
        placeholder={globalSearchPlaceholder}
        categories={categories}
        labels={globalSearchLabels}
        activeCategory={activeCategory}
        onCategoryChange={(categoryId) => {
          setSelectedFilterIds((current) => applyCategoryToFilterIds(current, categoryId));
        }}
        status="idle"
        activeFilters={activeFilters}
        selectedFilterIds={selectedFilterIds}
        onFiltersChange={setSelectedFilterIds}
        onRemoveFilter={(filterId) => {
          setSelectedFilterIds((current) => current.filter((id) => id !== filterId));
        }}
        onClearFilters={() => setSelectedFilterIds([])}
        onSearchAll={() => setSelectedFilterIds([])}
        suggestedActions={globalSearchSuggestedActionsFixture}
        recentItems={globalSearchRecentItemsFixture}
        onSelectResult={closeSearch}
        onSelectAction={closeSearch}
      />
    </>
  );
};

export default ShellTopNav;
